// =====================================================================================
// INVRT Radar — web console — device fingerprint capture (IPQS, capture-only)
//
// CE3.0 EVIDENCE, NOT FRAUD SCORING. Visa's Compelling Evidence 3.0 dispute rule lets a
// merchant defeat a "friendly fraud" chargeback by producing evidence — a device id that
// matches a prior undisputed purchase from the same customer is exactly that evidence.
// POST /v1/packs/checkout's own contract (routes/checkout.ts, CheckoutRequestSchema) says
// it plainly: `device_fingerprint` is "opaque to Radar... stored verbatim as CE3.0 evidence
// and never parsed, scored or acted on". This hook exists ONLY to capture that id at the
// moment of purchase and hand it to the Credits tab as component state. It never blocks,
// delays or judges the purchase itself — Radar does no fraud scoring client-side, and a
// customer whose browser blocks the script (an ad blocker, a privacy extension, third-party
// cookies off) still buys normally with no fingerprint sent.
//
// The concrete IPQS snippet URL is deploy-time configuration (VITE_IPQS_TRACKER_URL — see
// web/.env.example). Absent it, this hook does nothing at all: no script tag, no network
// request, no global left behind. The accessor that reads the id back out (`readDeviceId`
// below) is deliberately the ONE place that would need to change if IPQS's own snippet
// contract (global name, property, timing) differs from the placeholder assumed here —
// nothing beyond "the script exposes a device id on a window global once it is ready" is
// invented.
// =====================================================================================

import { useEffect, useRef, useState } from "react";

const SCRIPT_ID = "ipqs-device-tracker";
const POLL_INTERVAL_MS = 250;
const POLL_TIMEOUT_MS = 4_000;

declare global {
  interface Window {
    /**
     * PLACEHOLDER GLOBAL. IPQS's own snippet decides its real name — confirm against the
     * actual tracker script at deploy time and adjust `readDeviceId` below if it differs.
     */
    __ipqsDeviceId?: string;
  }
}

function readDeviceId(): string | undefined {
  if (typeof window === "undefined") return undefined;
  const value = window.__ipqsDeviceId;
  return typeof value === "string" && value.trim() !== "" ? value : undefined;
}

/**
 * Lazily injects the IPQS tracker script — only once a consumer mounts this hook, e.g. the
 * Credits tab, never at app boot — and surfaces the device id it exposes, once available.
 * Returns undefined whenever there is nothing to send: a missing env var, a blocked script,
 * or a snippet that has not finished collecting signals yet. Never throws.
 */
export function useDeviceFingerprint(): string | undefined {
  const [deviceId, setDeviceId] = useState<string | undefined>(() => readDeviceId());
  const injected = useRef(false);

  useEffect(() => {
    if (deviceId) return;

    const trackerUrl = import.meta.env.VITE_IPQS_TRACKER_URL;
    if (!trackerUrl) return; // Absent env → silently omit, exactly as documented.

    let cancelled = false;
    let pollHandle: ReturnType<typeof setInterval> | undefined;
    let timeoutHandle: ReturnType<typeof setTimeout> | undefined;

    function startPolling(): void {
      pollHandle = setInterval(() => {
        const found = readDeviceId();
        if (found && !cancelled) {
          setDeviceId(found);
          if (pollHandle) clearInterval(pollHandle);
          if (timeoutHandle) clearTimeout(timeoutHandle);
        }
      }, POLL_INTERVAL_MS);
      timeoutHandle = setTimeout(() => {
        if (pollHandle) clearInterval(pollHandle);
        // Gives up quietly: this is capture-only, so a slow or blocked script just means no
        // fingerprint is sent on this purchase — never a blocked or delayed Buy button.
      }, POLL_TIMEOUT_MS);
    }

    if (!injected.current && !document.getElementById(SCRIPT_ID)) {
      injected.current = true;
      const script = document.createElement("script");
      script.id = SCRIPT_ID;
      script.src = trackerUrl;
      script.async = true;
      script.onload = startPolling;
      script.onerror = () => {
        // A blocked or failed load is not an error state for this feature — it is simply
        // "no fingerprint available", the same outcome as the env var being unset.
      };
      document.body.appendChild(script);
    } else {
      startPolling();
    }

    return () => {
      cancelled = true;
      if (pollHandle) clearInterval(pollHandle);
      if (timeoutHandle) clearTimeout(timeoutHandle);
    };
  }, [deviceId]);

  return deviceId;
}
